import { useContext, useEffect, useMemo, useState } from 'react';
import WiseDriveLogoNew from '../../assets/images/header/wiseDrivelogoNew.png';
import './SampleReportNew.css';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  MobileStepper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Check,
  Close,
  Commit,
  DescriptionOutlined,
  ExpandMore,
  InfoOutlined,
  Launch,
  LocalPoliceOutlined,
  NavigateBefore,
  NavigateNext,
  TireRepairOutlined,
} from '@mui/icons-material';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import chennai from '../../assets/icons/chennai.png';
import hyderabad from '../../assets/icons/hyderabad.png';
import mumbai from '../../assets/icons/mumbai.png';
import delhi from '../../assets/icons/delhi.png';
import pune from '../../assets/icons/Pune.webp';
import bangalore from '../../assets/icons/bangalore.png';
import noida from '../../assets/icons/Noida.png';
import gurguaon from '../../assets/icons/gurguaon.png';
import { PortalContext } from '../../context/carPortalContext';
import useCombinedCategories from './EditReport/useCategories';
import { MyContext } from '../../context';

const GOOGLE_TRANSLATE_API_KEY = 'AIzaSyAUaSH3CpF-SlJGu26JDaubk_Go2PZN4x8'; // Replace with your API key
const TRANSLATE_API_URL = `https://translation.googleapis.com/language/translate/v2?key=${GOOGLE_TRANSLATE_API_KEY}`;

// In-memory cache for translations
const translationCache =
  JSON.parse(localStorage.getItem('translationCache')) || {};

// Translation function
const translateText = async (text, targetLanguage) => {
  if (!text) return text;
  // Create a cache key based on text and target language
  const cacheKey = `${text}::${targetLanguage}`;
  // Check if translation exists in cache
  if (translationCache[cacheKey]) {
    return translationCache[cacheKey];
  }
  try {
    const response = await fetch(TRANSLATE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        q: text,
        target: targetLanguage, // 'en' for English, 'ms' for Malay
        format: 'text',
      }),
    });
    const data = await response.json();
    if (data.data && data.data.translations) {
      const translatedText = data.data.translations[0].translatedText;
      // Store translation in cache
      translationCache[cacheKey] = translatedText;
      localStorage.setItem(
        'translationCache',
        JSON.stringify(translationCache)
      );
      return translatedText;
    }
    return text; // Fallback to original text if translation fails
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Fallback to original text
  }
};

// Helper function to translate nested objects
const translateNestedObject = async (obj, targetLanguage) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) {
    return await translateArray(obj, targetLanguage);
  }
  const translatedObj = {};
  for (const key in obj) {
    if (typeof obj[key] === 'string') {
      translatedObj[key] = await translateText(obj[key], targetLanguage);
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      translatedObj[key] = await translateNestedObject(
        obj[key],
        targetLanguage
      );
    } else {
      translatedObj[key] = obj[key];
    }
  }
  return translatedObj;
};

// Helper function to translate arrays
const translateArray = async (arr, targetLanguage) => {
  if (!arr) return arr;
  return Promise.all(
    arr.map(async (item) => {
      if (typeof item === 'string') {
        return await translateText(item, targetLanguage);
      } else if (typeof item === 'object' && item !== null) {
        return await translateNestedObject(item, targetLanguage);
      }
      return item;
    })
  );
};

// Helper function to translate inspectionData (parsed JSON string)
const translateInspectionData = async (inspectionData, targetLanguage) => {
  if (!inspectionData) return inspectionData;
  try {
    const parsedData = JSON.parse(inspectionData);
    const translatedData = await Promise.all(
      parsedData.map(async (category) => {
        const translatedCategory = { ...category };
        translatedCategory.titile = await translateText(
          category.titile,
          targetLanguage
        );
        translatedCategory.questionary = await Promise.all(
          category.questionary.map(async (questionary) => {
            const translatedQuestionary = { ...questionary };
            if (questionary.questions) {
              translatedQuestionary.questions = await translateNestedObject(
                questionary.questions,
                targetLanguage
              );
            }
            if (questionary.sub_question) {
              translatedQuestionary.sub_question = await translateNestedObject(
                questionary.sub_question,
                targetLanguage
              );
            }
            return translatedQuestionary;
          })
        );
        return translatedCategory;
      })
    );
    return JSON.stringify(translatedData);
  } catch (error) {
    console.error('Error translating inspectionData:', error);
    return inspectionData;
  }
};

const Header = ({ regNo, language, handleLanguageChange, country }) => {
  return (
    <div className="header">
      <div className="header__container">
        <div className="header__logo">
          <img
            loading="lazy"
            src={WiseDriveLogoNew}
            alt="Orange Health Labs"
            className="header__logo-img"
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '40px' }}>
          {country && country !== 'IN' && (
            <Select
              value={language}
              onChange={(event) =>
                handleLanguageChange(event, event.target.value)
              }
              displayEmpty
              sx={{
                backgroundColor: '#003cc5',
                color: '#fff',
                borderRadius: '8px',
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 600,
                fontSize: '14px',
                height: '32px',
                '& .MuiSelect-select': {
                  padding: '4px 12px',
                  paddingRight: '32px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-icon': {
                  color: '#fff',
                },
                '&:hover': {
                  backgroundColor: '#002a8e',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: '#f5f5f5',
                    '& .MuiMenuItem-root': {
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 600,
                      color: '#000',
                      '&:hover': {
                        backgroundColor: '#e0e0e0',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e0e0e0',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="ms">Malay</MenuItem>
              <MenuItem value="th">Thailand</MenuItem>
            </Select>
          )}
          <div className="header__number">
            <p>{regNo?.toUpperCase()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({
  handleOpenModal,
  summaryData,
  inspectionDetails,
  transitionData,
}) => {
  const [openDialog, setOpenDialog] = useState(false);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const overallRating =
    summaryData?.overallRating ?? summaryData?.aiInsights?.overAllRating;
  return (
    <div className="summary-card-container">
      {/* Blue Background Container */}
      <div className="summary-card__background">
        {/* Summary Card */}
        <div className="summary-card">
          <div className="summary-card__top-bulge"></div>
          <div className="summary-card__content">
            {/* Customer Information */}
            <h2 className="summary-card__customer-name">
              {summaryData?.customerName ?? '-'}
            </h2>
            <p className="summary-card__customer-info">
              {summaryData?.inspectionDetails?.inspectionDate ?? '-'} |{' '}
              {summaryData?.inspectionDetails?.location ?? '-'}
            </p>

            <p
              className="summary-card__customer-contact"
              style={{ fontWeight: 600 }}
            >
              {summaryData?.carRegistrationNumber?.toUpperCase() ?? '-'}
              {/* <button
                className="summary-card__full-details"
                onClick={handleOpenModal}
              >
                <Launch fontSize="small" />
              </button> */}
            </p>

            {/* Divider */}
            <div className="summary-card__divider"></div>

            {/* Report Details */}
            <div className="summary-card__details">
              <div className="detail-row-report">
                <div className="detail-row">
                  <span className="detail-label">
                    {transitionData?.reportFor}
                  </span>
                  <span className="detail-value">
                    {summaryData?.inspectionDetails?.packageName ?? '-'}
                  </span>
                </div>

                <div
                  className="detail-row side-by-side"
                  onClick={handleOpenDialog}
                  style={{ cursor: 'pointer', color: '#003cc5' }}
                >
                  <span className="detail-label">
                    {transitionData?.inspectedBy}
                  </span>
                  <span className="detail-value" style={{ color: '#003cc5' }}>
                    Wisedrive
                  </span>
                </div>
              </div>
              <div className="detail-row-market full-width">
                <span className="detail-label">
                  {transitionData?.marketValue}
                </span>
                <span
                  className="detail-value badge-highlight"
                  style={{ fontWeight: 800, fontSize: '16px' }}
                >
                  {summaryData?.marketValue ||
                    summaryData?.aiInsights?.marketValue ||
                    '-'}
                </span>
              </div>
              <div className="detail-row-overall">
                <div className="detail-row">
                  <span className="detail-label">
                    {transitionData?.overallRating}
                  </span>
                  <span
                    className="detail-value overall-rating"
                    style={{
                      color:
                        overallRating <= 4
                          ? 'red'
                          : overallRating <= 7
                          ? 'orange'
                          : 'green', // Dynamic color based on rating
                      fontWeight: 'bold',
                    }}
                  >
                    <strong>{overallRating || '-'}</strong> / 10
                  </span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">
                    {transitionData?.recommendedToBuy}
                  </span>
                  <span
                    className="detail-value badge-no"
                    style={{
                      backgroundColor:
                        summaryData?.recommendedToBuy === 'Yes' ||
                        summaryData?.aiInsights?.recommendedToBuy === 'Yes'
                          ? '#ccf0e1'
                          : '#fee2e2',
                      color:
                        summaryData?.recommendedToBuy === 'Yes' ||
                        summaryData?.aiInsights?.recommendedToBuy === 'Yes'
                          ? '#065f46'
                          : '#b91c1c',
                    }}
                  >
                    {summaryData?.recommendedToBuy ||
                      summaryData?.aiInsights?.recommendedToBuy ||
                      '-'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: '16px',
            },
          }}
        >
          <DialogTitle sx={{ color: '#003cc5' }}>
            {transitionData?.inspectionDetailsTitle}
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ paddingBottom: '30px' }}>
            <Grid container spacing={2}>
              {/* Row 1 */}
              <Grid item xs={6}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: '#4b5563',
                    fontSize: '0.9rem',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  {transitionData?.requestedDate}:
                </Typography>
                <Box display="flex" alignItems="end">
                  <Typography
                    variant="body1"
                    sx={{
                      paddingTop: '4px !important',
                      color: '#000',
                      fontSize: '15px',
                      fontWeight: 700,
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    {inspectionDetails?.inspectionRequestedDate}
                  </Typography>
                  {/* <Check sx={{ color: "green", marginLeft: "8px" }} /> */}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: '#4b5563',
                    fontSize: '0.9rem',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  {transitionData?.inspectionDate}:
                </Typography>
                <Box display="flex" alignItems="end">
                  <Typography
                    variant="body1"
                    sx={{
                      paddingTop: '4px !important',
                      color: '#000',
                      fontSize: '15px',
                      fontWeight: 700,
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    {inspectionDetails?.inspectionDate}
                  </Typography>
                  {/* <Check sx={{ color: "green", marginLeft: "8px" }} /> */}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: '#4b5563',
                    fontSize: '0.9rem',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  {transitionData?.mechanicName}:
                </Typography>
                <Box display="flex" alignItems="end">
                  <Typography
                    variant="body1"
                    sx={{
                      paddingTop: '4px !important',
                      color: '#000',
                      fontSize: '15px',
                      fontWeight: 700,
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    {inspectionDetails?.mechanicName}
                  </Typography>
                  {/* <Check sx={{ color: "green", marginLeft: "8px" }} /> */}
                </Box>
              </Grid>

              {/* Row 2 */}

              <Grid item xs={6}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: '#4b5563',
                    fontSize: '0.9rem',
                    fontFamily: 'Manrope, sans-serif',
                  }}
                >
                  {transitionData?.location}:
                </Typography>
                <Box display="flex" alignItems="end">
                  <Typography
                    variant="body1"
                    sx={{
                      paddingTop: '4px !important',
                      color: '#000',
                      fontSize: '15px',
                      fontWeight: 700,
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    {inspectionDetails?.location}
                  </Typography>
                  {/* <Check sx={{ color: "green", marginLeft: "8px" }} /> */}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

const VehicleInformation = ({ vehicleInfo, transitionLang }) => {
  return (
    <div className="key-information-container" style={{ margin: '0px' }}>
      <h3 className="vehicle-information__title">
        {transitionLang?.vehicleInformationTitle}
      </h3>
      <div className="vehicle-information__grid">
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.make}:</span>
          <span className="vehicle-info-value">{vehicleInfo?.make ?? '-'}</span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.owners}:</span>
          <span className="vehicle-info-value highlighted">
            {vehicleInfo?.owners}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.model}:</span>
          <span className="vehicle-info-value">
            {vehicleInfo?.model ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">
            {transitionLang?.transmission}:
          </span>
          <span className="vehicle-info-value">
            {vehicleInfo?.transmission ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.year}:</span>
          <span className="vehicle-info-value">
            {vehicleInfo?.mfgDate ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.regNo}:</span>
          <span className="vehicle-info-value">
            {vehicleInfo?.regNo ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.mfgDate}:</span>
          <span className="vehicle-info-value">
            {vehicleInfo?.mfgDate ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.fuel}:</span>
          <span className="vehicle-info-value">{vehicleInfo?.fuel ?? '-'}</span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.colour}:</span>
          <span className="vehicle-info-value">
            {vehicleInfo?.colour ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">{transitionLang?.regDate}:</span>
          <span className="vehicle-info-value">
            {vehicleInfo?.regDate ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">
            {transitionLang?.engineCC}:
          </span>
          <span className="vehicle-info-value">
            {vehicleInfo?.engineCC ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">
            {transitionLang?.engineNumber}:
          </span>
          <span className="vehicle-info-value">
            {vehicleInfo?.engineNumber ?? '-'}
          </span>
        </div>
        <div className="vehicle-info-item">
          <span className="vehicle-info-label">
            {transitionLang?.chassisNo}:
          </span>
          <span className="vehicle-info-value">
            {vehicleInfo?.chassisNumber ?? '-'}
          </span>
        </div>
      </div>
    </div>
  );
};

const VehicleInformationModal = ({ open, handleClose }) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '12px', // Set border radius here
          overflow: 'hidden', // Helps ensure the rounded corners are visible
        },
      }}
    >
      <DialogTitle
        className="vehicle-information__title"
        sx={{ fontFamily: 'Manrope, sans-serif', color: '#003cc5' }}
      >
        Vehicle Information
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: 0, // Remove default padding
          marginBottom: '20px',
        }}
      >
        <VehicleInformation />
      </DialogContent>
    </Dialog>
  );
};

const InspectionSummary = ({ summary, vehicleInfo, transitionData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const text = '';
  const previewLimit = 200;
  const shouldShowViewMore = summary?.length > previewLimit;
  return (
    <div className="inspection-summary-container">
      {/* Inspection Summary Section */}
      <div className="inspection-summary">
        <h3 className="inspection-summary__title">
          <Commit color="disabled" /> {transitionData?.inspectionSummaryTitle}{' '}
          <Commit color="disabled" />
        </h3>
        <p className="inspection-summary__checkpoints">
          {transitionData?.checkpointsInspected}
        </p>
      </div>
      <VehicleInformation
        vehicleInfo={vehicleInfo}
        transitionLang={transitionData}
      />
      {/* Interpretations Section */}
      <div className="interpretations">
        <h4 className="interpretations__title">
          {transitionData?.assessmentTitle}
        </h4>
        <p className="interpretations__text">
          {isExpanded || !shouldShowViewMore
            ? summary
            : `${summary.slice(0, previewLimit)}...`}
          {shouldShowViewMore && (
            <span
              onClick={() => setIsExpanded(!isExpanded)}
              style={{ color: '#003cc5', cursor: 'pointer' }}
            >
              &nbsp;{isExpanded ? 'Show Less' : 'View More'}
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

const EstimateCard = ({ title, price, handleOpenModal }) => (
  <Card
    sx={{
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'flex-start',
      padding: '16px 8px',
      margin: '16px 8px',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
      minWidth: '150px',
      borderRadius: '8px',
      position: 'relative',
    }}
    onClick={handleOpenModal}
    className="estimate-card"
  >
    <CardContent
      sx={{
        display: 'flex',
        padding: '2px 12px 24px',
      }}
      className="estimate-card-content"
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          fontSize: '16px',
          fontFamily: 'Manrope, sans-serif',
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 800,
          color: parseInt(price.replace('Rs ', '')) >= 5000 ? 'red' : '#000',
          fontFamily: 'Manrope, sans-serif',
        }}
      >
        {price}
      </Typography>
    </CardContent>
    <Button
      variant="contained"
      onClick={handleOpenModal}
      sx={{
        textTransform: 'none',
        backgroundColor: '#003cc5',
        alignSelf: 'end',
        color: '#fff',
        fontWeight: 700,
        fontSize: '12px',
        padding: '4px 6px',
        borderRadius: '8px 0px',
        position: 'absolute',
        bottom: '0px',
        right: '0px',
        marginTop: '16px',
        fontFamily: 'Manrope, sans-serif',
        '&:hover': {
          backgroundColor: '#003cc5',
        },
      }}
      className="view-estimate-button"
    >
      View Estimate
    </Button>
  </Card>
);

const EstimateModal = ({
  open,
  handleClose,
  estimates,
  title,
  transitionData,
}) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '16px', // Set border radius for consistency
          padding: '16px',
        },
      }}
    >
      <DialogTitle sx={{ color: '#003cc5' }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className="estimate_model_content" sx={{ padding: 0 }}>
        {estimates.length > 0 ? (
          estimates.map((estimate, index) => (
            <Box
              key={index}
              display="flex"
              alignItems="center"
              py={2}
              borderBottom={
                index === estimates.length - 1 ? 'none' : '1px solid #e0e0e0'
              }
            >
              {/* Icon: Labour or Spare Parts */}
              <Box
                sx={{
                  width: '52px', // Fixed width
                  height: '52px', // Fixed height
                  minWidth: '52px', // Ensures the width doesn't shrink
                  backgroundColor: '#f5f5f5',
                  borderRadius: '50%',
                  fontFamily: 'Manrope, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '16px',
                  flexShrink: 0, // Prevent shrinking in flex layout
                  '@media (max-width: 468px)': {
                    width: '52px !important', // Adjusted width for mobile
                    height: '52px', // Adjusted height for mobile
                    minWidth: '52px', // Ensure minimum width doesn't shrink
                  },
                }}
                className="estimate_icon"
              >
                {estimate.type === 'Labour' ? (
                  <img
                    loading="lazy"
                    alt=""
                    src="https://img.icons8.com/?size=100&id=BDXhhTXkmeSR&format=png&color=000000"
                    height={'30px'}
                  />
                ) : (
                  <img
                    loading="lazy"
                    alt=""
                    src="https://img.icons8.com/?size=100&id=zrMKeqZpbojb&format=png&color=000000"
                    height={'30px'}
                  />
                )}
              </Box>

              {/* Details: Description, Amount, and Type */}
              <Box>
                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: '#333333',
                    fontFamily: 'Manrope, sans-serif',
                    padding: 0,
                  }}
                >
                  {estimate.title}
                </Typography>

                {/* Cost and Charge Type */}
                <Box display="flex" alignItems="center">
                  {/* Cost in Green */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#000',
                      fontWeight: 600,
                      marginRight: '8px',
                      paddingTop: '2px',
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: '16px',
                    }}
                  >
                    {`${estimate.price}`}
                  </Typography>
                  {/* Labour or Spare Parts Label in Grey */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#757575',
                      fontFamily: 'Manrope, sans-serif',
                      paddingTop: '2px',
                    }}
                  >
                    {estimate.category === 'Labour'
                      ? 'Labour Charge'
                      : 'Spare Parts'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', padding: '20px' }}
          >
            {transitionData?.noEstimate}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

const InsuranceDetailsModal = ({
  open,
  onClose,
  insuranceDetails,
  transitionData,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="xs"
    PaperProps={{
      sx: {
        borderRadius: '16px',
      },
    }}
  >
    <DialogTitle sx={{ color: '#003cc5' }}>
      {transitionData?.insuranceDetails}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Close />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ marginBottom: '20px' }}>
      <Grid container spacing={2}>
        <Grid item xs={6} className="insurance-model">
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontFamily: 'Manrope, sans-serif',
              color: '#4b5563',
              fontSize: '0.9rem',
            }}
          >
            {transitionData?.insurer}:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              fontFamily: 'Manrope, sans-serif',
              color: '#000',
              fontSize: '15px',
            }}
          >
            {insuranceDetails.insurer}
          </Typography>
        </Grid>
        <Grid item xs={6} className="insurance-model">
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontFamily: 'Manrope, sans-serif',
              color: '#4b5563',
              fontSize: '0.9rem',
            }}
          >
            {transitionData?.policyNumber}:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              fontFamily: 'Manrope, sans-serif',
              color: '#000',
              fontSize: '15px',
            }}
          >
            {insuranceDetails.policyNumber}
          </Typography>
        </Grid>
        <Grid item xs={6} className="insurance-model">
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontFamily: 'Manrope, sans-serif',
              color: '#4b5563',
              fontSize: '0.9rem',
            }}
          >
            {transitionData?.expiryDate}:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              fontFamily: 'Manrope, sans-serif',
              color: '#000',
              fontSize: '15px',
            }}
          >
            {insuranceDetails.expiryDate}
          </Typography>
        </Grid>
        <Grid item xs={6} className="insurance-model">
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontFamily: 'Manrope, sans-serif',
              color: '#4b5563',
              fontSize: '0.9rem',
            }}
          >
            {transitionData?.expiresIn}:
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              fontFamily: 'Manrope, sans-serif',
              // color: "red",
              fontSize: '15px',
            }}
          >
            {insuranceDetails.expiresIn}
          </Typography>
        </Grid>
      </Grid>
    </DialogContent>
  </Dialog>
);

const DentsModal = ({ open, onClose, images, dentsData, transitionData }) => {
  const formatDetail = (detail, type) => {
    // Check if detail includes "Dents" or "Scratches"
    const hasDentsOrScratches = detail && /dents|scratches/i.test(detail);
    return detail ? (
      <span>
        <span style={{ fontWeight: 'bold' }}>{detail}</span>
        {!hasDentsOrScratches && ` ${type}`}
      </span>
    ) : (
      `No ${type}`
    );
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle sx={{ color: '#003cc5' }}>
        {transitionData?.dentsDetails}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: '30px' }}>
        <Grid container spacing={2}>
          {dentsData ? (
            Object.entries(dentsData).map(([panel, details]) => {
              if (panel !== 'dents') {
                // Exclude the general 'dents' flag
                return (
                  <Grid item xs={6} key={panel}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#4b5563',
                        fontSize: '0.9rem',
                        fontFamily: 'Manrope, sans-serif',
                      }}
                    >
                      {panel.replace(/-/g, ' ')}:
                    </Typography>
                    <Box display="flex" flexDirection="column">
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#000',
                          fontSize: '15px',
                          fontWeight: 700,
                          fontFamily: 'Manrope, sans-serif',
                        }}
                      >
                        {formatDetail(details.Dents, 'dents')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#000',
                          fontSize: '15px',
                          fontWeight: 700,
                          fontFamily: 'Manrope, sans-serif',
                        }}
                      >
                        {formatDetail(details.Scratches, 'scratches')}
                      </Typography>
                    </Box>
                  </Grid>
                );
              }
            })
          ) : (
            <Grid item xs={12}>
              <Typography>No dent details available.</Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const KeyInformation = ({
  initialKeyInfo,
  insuranceData: initialInsuranceData,
  dentsData: initialDentsData,
  tyreLifeData: initialTyreLifeData,
  majorRepairData: initialMajorRepairData,
  minorRepairData: initialMinorRepairData,
  kmsDriven: initialKmsDriven,
  reportData,
  transitionData,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [isTyreLifeModalOpen, setIsTyreLifeModalOpen] = useState(false);
  const [isDentsModalOpen, setIsDentsModalOpen] = useState(false);
  const [openInsuranceModal, setOpenInsuranceModal] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const {
    keyInfo,
    setKeyInfo,
    updateKeyInfo,
    insuranceData,
    updateInsuranceData,
    dentsData,
    setDentsData,
    tyreLifeData,
    updateTyreLifeData,
    majorRepairs,
    setMajorRepairs,
    minorRepairs,
    setMinorRepairs,
    removeRepair,
    hasDentDetails,
    averageTyreLife,
    hasTyreLifeData,
    computedExteriorCondition,
    computedInteriorCondition,
    floodDamageAnswer,
    majorAccidentAnswer,
  } = useContext(PortalContext);
  useEffect(() => {
    if (initialKeyInfo) {
      setKeyInfo((prev) => ({
        ...prev,
        ...initialKeyInfo,
        majorAccident:
          majorAccidentAnswer || initialKeyInfo.majorAccident || 'NO',
        floodDamage: floodDamageAnswer || initialKeyInfo.floodDamage || 'NO',
        engineCondition: initialKeyInfo.engineCondition || 'AVERAGE',
        transmission: initialKeyInfo.transmission || 'AVERAGE',
        exteriorCondition:
          computedExteriorCondition ||
          initialKeyInfo.exteriorCondition ||
          'AVERAGE',
        interiorCondition:
          computedInteriorCondition ||
          initialKeyInfo.interiorCondition ||
          'AVERAGE',
        kmsDriven: initialKmsDriven,
      }));
    }
    if (initialInsuranceData) {
      updateInsuranceData(initialInsuranceData);
    }
    if (initialDentsData) {
      setDentsData((prev) => ({ ...prev, ...initialDentsData }));
    }
    if (initialTyreLifeData) {
      updateTyreLifeData((prev) => ({ ...prev, ...initialTyreLifeData }));
    }
    if (initialMajorRepairData) {
      setMajorRepairs([...initialMajorRepairData]);
    }
    if (initialMinorRepairData) {
      setMinorRepairs([...initialMinorRepairData]);
    }
  }, [
    initialInsuranceData,
    initialDentsData,
    initialTyreLifeData,
    initialMajorRepairData,
    initialMinorRepairData,
  ]);
  const calculateInsuranceExpiry = (expiryDate) => {
    const today = new Date();
    const expirationDate = new Date(expiryDate);
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays < 0 ? (
      <span style={{ color: 'red' }}>
        Expired {Math.abs(diffDays)} days ago
      </span>
    ) : (
      <span style={{ color: 'green' }}>Expires in {diffDays} days</span>
    );
  };

  const insuranceStatus = useMemo(() => {
    const today = new Date();
    const expiry = new Date(insuranceData?.expiryDate);
    return expiry >= today ? 'Valid' : 'Expired';
  }, [insuranceData?.expiryDate]);

  const insuranceDetails = {
    insurer: insuranceData?.insurer || transitionData?.notAvailable,
    policyNumber: insuranceData?.policyNumber || transitionData?.notAvailable,
    expiryDate: insuranceData?.expiryDate || transitionData?.notAvailable,
    expiresIn: insuranceData?.expiryDate
      ? calculateInsuranceExpiry(insuranceData?.expiryDate)
      : transitionData?.notAvailable,
    status: insuranceData?.expiryDate
      ? new Date(insuranceData?.expiryDate) >= new Date()
        ? transitionData?.valid
        : transitionData?.expired
      : transitionData?.notAvailable,
  };

  const handleOpenTyreLifeModal = () => {
    setIsTyreLifeModalOpen(true);
  };

  const handleCloseTyreLifeModal = () => {
    setIsTyreLifeModalOpen(false);
  };

  const handleOpenDentsModal = () => {
    setIsDentsModalOpen(true);
  };

  const handleCloseDentsModal = () => {
    setIsDentsModalOpen(false);
  };

  const handleOpenInsuranceModal = () => {
    setOpenInsuranceModal(true);
  };

  const handleCloseInsuranceModal = () => {
    setOpenInsuranceModal(false);
  };

  const handleOpenModal = (repairType) => {
    setSelectedEstimate(
      repairType === 'Major Repairs' ? majorRepairs : minorRepairs
    );
    setModalTitle(
      `${
        repairType === 'Major Repairs'
          ? transitionData?.majorRepairEstimate
          : transitionData?.minorRepairEstimate
      }`
    );
    setOpenModal(true);
  };
  const majorRepairAmount = 7000;

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const images = [
    {
      original: 'https://via.placeholder.com/600x300',
      description: 'Front-Left',
    },
    {
      original: 'https://via.placeholder.com/600x300',
      description: 'Front-Right',
    },
    {
      original: 'https://via.placeholder.com/600x300',
      description: 'Rear-Left',
    },
    {
      original: 'https://via.placeholder.com/600x300',
      description: 'Rear-Right',
    },
  ];

  const calculateTotal = (repairs) => {
    if (!Array.isArray(repairs)) {
      console.error('Invalid repairs data:', repairs);
      return 0;
    }
    return repairs.reduce((acc, repair) => {
      const costString = repair?.price || '';
      const numericValue = parseFloat(costString.replace(/[^\d.-]/g, ''));
      return acc + (isNaN(numericValue) ? 0 : numericValue);
    }, 0);
  };
  // const totalMajorRepairs = useMemo(
  //   () => calculateTotal(keyInfo?.majorRepair),
  //   [majorRepairs]
  // );
  // const totalMinorRepairs = useMemo(
  //   () => calculateTotal(keyInfo?.minorRepair),
  //   [minorRepairs]
  // );
  const marketValue =
    reportData?.aiInsights?.marketValue || reportData?.marketValue || '';
  const selectedCurrency = marketValue.toString().includes('RM')
    ? 'MYR'
    : 'INR';
  const locale = selectedCurrency === 'MYR' ? 'en-MY' : 'en-IN';
  const totalMajorRepairs = useMemo(() => {
    const total = calculateTotal(majorRepairs);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: selectedCurrency,
      maximumFractionDigits: 0,
    }).format(total);
  }, [majorRepairs]);

  const totalMinorRepairs = useMemo(() => {
    const total = calculateTotal(minorRepairs);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: selectedCurrency,
      maximumFractionDigits: 0,
    }).format(total);
  }, [minorRepairs]);

  const hasDents = Object.keys(dentsData || {}).length > 0;

  const handleInfoDialogOpen = () => {
    if (keyInfo?.majorAccident === 'NO') {
      setInfoDialogOpen(true);
    }
  };
  const handleInfoDialogClose = () => setInfoDialogOpen(false);

  const tyreLifeSource =
    keyInfo?.tyreLife && Object.keys(keyInfo.tyreLife).length > 0
      ? keyInfo.tyreLife
      : tyreLifeData;

  const tyreLifeSourceFiltered = tyreLifeSource
    ? Object.entries(tyreLifeSource).filter(
        ([key]) => key.toLowerCase() !== 'average'
      )
    : [];

  const getTranslatedCondition = (condition) => {
    switch (condition?.toLowerCase()) {
      case 'good':
        return transitionData?.good || condition;
      case 'bad':
        return transitionData?.bad || condition;
      case 'average':
        return transitionData?.average || condition;
      case 'excellent':
        return transitionData?.excellent || condition;
      default:
        return transitionData?.average;
    }
  };
  return (
    <>
      <div className="key-information-container">
        <h3 className="key-information__title">
          {transitionData?.keyInformation}
        </h3>
        <div className="key-information__grid">
          <div className="vehicle-info-item">
            <span className="vehicle-info-label">
              {transitionData?.kmsDriven}
            </span>
            <span className="vehicle-info-value highlighted">
              {initialKmsDriven
                ? initialKmsDriven
                : keyInfo?.kmsDriven
                ? keyInfo?.kmsDriven
                : '-'}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.majorAccident}
            </span>

            <span
              className="key-info-value"
              style={{
                textDecoration:
                  keyInfo?.majorAccident === 'NO' ? 'underline' : '',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {majorAccidentAnswer.toLowerCase() === 'yes'
                ? transitionData?.yes
                : transitionData?.no}{' '}
              {keyInfo?.majorAccident === 'NO' && (
                <Tooltip
                  title="Click for more information"
                  onClick={handleInfoDialogOpen}
                >
                  <span
                    style={{
                      color: 'red',
                      cursor: 'pointer',
                      marginLeft: '4px', // Adjust spacing for the star
                      display: 'flex',
                    }}
                  >
                    <InfoOutlined style={{ fontSize: '20px' }} />
                  </span>
                </Tooltip>
              )}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.floodDamage}
            </span>
            <span className="key-info-value">
              {floodDamageAnswer.toLowerCase() === 'yes'
                ? transitionData?.yes
                : transitionData?.no}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.engineCondition}
            </span>
            <span className="key-info-value highlighted-yellow">
              {getTranslatedCondition(keyInfo?.engineCondition)}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.transmission}
            </span>
            <span className="key-info-value highlighted-yellow">
              {getTranslatedCondition(keyInfo?.transmission)}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.exteriorCondition}
            </span>
            <span className="key-info-value highlighted-red">
              {getTranslatedCondition(
                computedExteriorCondition || keyInfo?.exteriorCondition
              )}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.interiorCondition}
            </span>
            <span className="key-info-value highlighted-red">
              {getTranslatedCondition(
                computedInteriorCondition || keyInfo?.interiorCondition
              )}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.dentsScratches}
            </span>
            <span className="key-info-value highlighted-red">
              {hasDents || hasDentDetails
                ? transitionData?.yes
                : transitionData?.no}{' '}
              <span
                style={{
                  fontSize: '12px',
                  color: '#003cc5',
                  textDecoration: 'underline',
                  alignItems: 'baseline',
                }}
                onClick={handleOpenDentsModal}
              >
                {/* <TireRepairOutlined
                  fontSize="small"
                  sx={{ marginTop: "4px" }}
                /> */}
                {transitionData?.details}
              </span>
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.avgTyreLife}
            </span>
            <span
              className="key-info-value"
              style={{ display: 'flex', gap: '5px', alignItems: 'baseline' }}
            >
              {keyInfo?.tyreLife?.Average ?? tyreLifeData?.Average ?? 0}
              {hasTyreLifeData && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#003cc5',
                    textDecoration: 'underline',
                    alignItems: 'baseline',
                  }}
                  onClick={handleOpenTyreLifeModal}
                >
                  {/* <TireRepairOutlined
                  fontSize="small"
                  sx={{ marginTop: "4px" }}
                /> */}
                  {transitionData?.details}
                </span>
              )}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">{transitionData?.insurance}</span>
            <span
              className="key-info-value highlighted-red"
              style={{
                display: 'flex',
                gap: '5px',
                alignItems: 'baseline',
                color:
                  insuranceDetails?.status === 'Valid' ? '#388e3c' : '#d32f2f',
                cursor: 'pointer',
              }}
            >
              {insuranceDetails?.status}
              {insuranceDetails?.status !== transitionData?.notAvailable && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#003cc5',
                    textDecoration: 'underline',
                    alignItems: 'baseline',
                  }}
                  onClick={handleOpenInsuranceModal}
                >
                  {/* <TireRepairOutlined
                  fontSize="small"
                  sx={{ marginTop: "4px" }}
                /> */}
                  {transitionData?.details}
                </span>
              )}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.majorRepair}
            </span>
            <span
              className="key-info-value"
              style={{
                display: 'flex',
                gap: '5px',
                alignItems: 'baseline',
                color: '#000',
                fontWeight: '600',
              }}
            >
              {`${totalMajorRepairs}`}{' '}
              <span
                style={{
                  fontSize: '12px',
                  color: '#003cc5',
                  textDecoration: 'underline',
                }}
                onClick={() => handleOpenModal('Major Repairs')}
              >
                {/* <DescriptionOutlined
                  fontSize="small"
                  sx={{ marginTop: "4px" }}
                /> */}
                {transitionData?.estimate}
              </span>
            </span>
            {/* Implement Major and Minor Functionality here and add */}
          </div>
          <div className="key-info-item">
            <span className="key-info-label">
              {transitionData?.minorRepair}
            </span>
            <span
              className="key-info-value"
              style={{
                display: 'flex',
                gap: '5px',
                alignItems: 'baseline',
                fontWeight: '600',
              }}
            >
              {`${totalMinorRepairs}`}{' '}
              <span
                style={{
                  fontSize: '12px',
                  color: '#003cc5',
                  textDecoration: 'underline',
                }}
                onClick={() => handleOpenModal('Minor Repairs')}
              >
                {/* <DescriptionOutlined
                  fontSize="small"
                  sx={{ marginTop: "4px" }}
                /> */}
                {transitionData?.estimate}
              </span>
            </span>
          </div>
        </div>
      </div>
      <EstimateModal
        open={openModal}
        handleClose={handleCloseModal}
        estimates={selectedEstimate}
        title={modalTitle}
        transitionData={transitionData}
      />
      <InsuranceDetailsModal
        open={openInsuranceModal}
        onClose={handleCloseInsuranceModal}
        insuranceDetails={insuranceDetails}
        transitionData={transitionData}
      />
      <DentsModal
        open={isDentsModalOpen}
        onClose={handleCloseDentsModal}
        images={images}
        dentsData={keyInfo?.dentsOnBody ?? dentsData}
        transitionData={transitionData}
      />
      <Dialog
        open={isTyreLifeModalOpen}
        onClose={handleCloseTyreLifeModal}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#003cc5' }}>
          {transitionData?.tyreLifeDetails}
          <IconButton
            aria-label="close"
            onClick={handleCloseTyreLifeModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: '30px' }}>
          <Grid container spacing={2}>
            {tyreLifeSourceFiltered ? (
              tyreLifeSourceFiltered?.map(([tyre, lifePercentage]) => {
                if (tyre !== 'tyreLife') {
                  return (
                    <Grid item xs={6} key={tyre}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: '#4b5563',
                          fontSize: '0.9rem',
                          fontFamily: 'Manrope, sans-serif',
                        }}
                      >
                        {tyre.replace(/-/g, ' ')}:
                      </Typography>
                      <Box display="flex" alignItems="end">
                        <Typography
                          variant="body1"
                          sx={{
                            paddingTop: '4px !important',
                            color: '#000',
                            fontSize: '15px',
                            fontWeight: 700,
                            fontFamily: 'Manrope, sans-serif',
                          }}
                        >
                          {lifePercentage}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                }
              })
            ) : (
              <Grid item xs={12}>
                <Typography>{transitionData?.noTyreLife}</Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={infoDialogOpen}
        onClose={handleInfoDialogClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{transitionData?.majorAccidentInfo}</DialogTitle>
        <DialogContent>{transitionData?.majorBasic}</DialogContent>
      </Dialog>
    </>
  );
};

const AdditionalInformation = ({ rtoData, additionalChallans }) => {
  const [openModal, setOpenModal] = useState(false);
  const [openHypothecationModal, setOpenHypothecationModal] = useState(false);

  const calculateTotalChallans = (challans) => {
    return challans?.reduce((total, challan) => {
      const amount = parseFloat(
        challan?.amount.toString().replace(/Rs\. /, '')
      );
      return total + (isNaN(amount) ? 0 : amount);
    }, 0);
  };
  // const challans = [
  //   {
  //     icon: <LocalPoliceOutlined />,
  //     description: "Over Speeding",
  //     amount: "Rs. 500",
  //     date: "14th Nov 2024",
  //   },
  //   {
  //     icon: <LocalPoliceOutlined />,
  //     description: "Signal Jumping",
  //     amount: "Rs. 300",
  //     date: "15th Nov 2024",
  //   },
  // ];

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleOpenhypothecationModal = () => {
    setOpenHypothecationModal(true);
  };

  const handlehypothecationCloseModal = () => {
    setOpenHypothecationModal(false);
  };

  // Check if all RTO information is null or empty
  const hasRtoInformation =
    rtoData &&
    (rtoData.hypothecation ||
      rtoData.bankNOC ||
      rtoData.blacklistStatus ||
      (rtoData.challans && rtoData.challans.length > 0));

  // If no RTO information, don't render the section
  if (!hasRtoInformation) {
    return null;
  }

  return (
    <>
      <div className="key-information-container">
        <h3 className="key-information__title">RTO Verification</h3>
        <div className="key-information__grid">
          <div className="key-info-item">
            <span className="key-info-label">Traffic Challans</span>
            <span
              className="key-info-value"
              style={{ display: 'flex', gap: '5px', alignItems: 'baseline' }}
            >
              Rs.
              {calculateTotalChallans(
                additionalChallans || rtoData?.challans
              )}{' '}
              {calculateTotalChallans(
                additionalChallans || rtoData?.challans
              ) !== 0 && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#003cc5',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={handleOpenModal}
                >
                  Details
                </span>
              )}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">Hypothecation</span>
            <span className="key-info-value">
              {rtoData?.hypothecation
                ? rtoData.hypothecation === 'false'
                  ? 'No'
                  : 'Yes'
                : '-'}{' '}
              {rtoData?.hypothecation === 'true' && (
                <span
                  style={{
                    fontSize: '12px',
                    color: '#003cc5',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={handleOpenhypothecationModal}
                >
                  Details
                </span>
              )}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">Bank NOC</span>
            <span className="key-info-value highlighted-red">
              {rtoData?.bankNOC || '-'}
            </span>
          </div>
          <div className="key-info-item">
            <span className="key-info-label">Blacklist status</span>
            <span
              className="key-info-value highlighted-yellow"
              style={{
                color: !rtoData?.blacklistStatus
                  ? '#1a1a1a'
                  : rtoData.blacklistStatus === 'false'
                  ? '#1a1a1a'
                  : '#d32f2f',
              }}
            >
              {!rtoData?.blacklistStatus
                ? '-'
                : rtoData.blacklistStatus === 'false'
                ? 'No'
                : 'Yes'}
            </span>
          </div>
        </div>
      </div>
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#003cc5' }}>
          Traffic Challans Details
        </DialogTitle>
        <DialogContent>
          {additionalChallans?.length > 0 ? (
            additionalChallans
              ?.filter((challan) => challan.challanStatus !== 'Paid')
              ?.map((challan, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  py={2}
                  borderBottom={
                    index === additionalChallans?.length - 1
                      ? 'none'
                      : '1px solid #e0e0e0'
                  }
                >
                  {/* Police Icon */}
                  <Box
                    sx={{
                      width: '52px',
                      height: '52px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      // marginRight: "16px",
                    }}
                  >
                    <LocalPoliceOutlined />
                  </Box>

                  {/* Details: Description, Amount, and Date */}
                  <Box>
                    {/* Offense Description */}
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#333333',
                        fontFamily: 'Manrope, sans-serif',
                        padding: 0,
                        fontSize: '14px',
                      }}
                    >
                      {challan.offenseDetails}
                    </Typography>

                    {/* Amount and Date */}
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#000',
                          fontWeight: 600,
                          marginRight: '8px',
                          fontFamily: 'Manrope, sans-serif',
                          fontSize: '16px',
                          padding: 0,
                        }}
                      >
                        {challan.amount}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#757575',
                          fontFamily: 'Manrope, sans-serif',
                          padding: 0,
                        }}
                      >
                        {challan.challanDate}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
          ) : (
            <Typography variant="body2">No Challans Found</Typography>
          )}
        </DialogContent>
      </Dialog>
      {rtoData?.financier !== '' && (
        <Dialog
          open={openHypothecationModal}
          onClose={handlehypothecationCloseModal}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: '16px',
            },
          }}
        >
          <DialogTitle sx={{ color: '#003cc5' }}>Financiar Details</DialogTitle>
          <DialogContent>
            <Typography variant="subtitle1">
              {rtoData?.financier !== ''
                ? rtoData?.financier
                : 'No Data Available'}
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const cardsData = [
  { title: 'Exterior', score: 6, expectedValue: 6, points: 26 },
  { title: 'Interior', score: 8, expectedValue: 8, points: 40 },
  { title: 'Engine', score: 1, expectedValue: 7, points: 10 },
  { title: 'Suspension', score: 5, expectedValue: 5, points: 20 },
  { title: 'Brakes', score: 9, expectedValue: 9, points: 8 },
];

const sampleSteps = [
  {
    text: 'Step 1: This is a text-only step. This is a text-only step, This is a text-only step. This is a text-only step,This is a text-only step',
  },
  {
    text: 'Step 2: This step contains text with images.',
    images: [
      'https://via.placeholder.com/600x300',
      'https://via.placeholder.com/400x200',
    ],
  },
  {
    text: 'Step 3: This step contains a video.',
    video: 'https://www.youtube.com/embed/lB0ZTQcws_4?si=OgremihHWhdjFGbB',
  },
];

const tyreImages = [
  'https://via.placeholder.com/600x300',
  'https://via.placeholder.com/400x200',
];

const CollapsibleCard = ({
  title,
  categoryKey,
  points,
  categoryData,
  cardData,
  transitionData,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [hiddenSteps, setHiddenSteps] = useState({}); // Track hidden states for steps
  const score = cardData?.[categoryKey] || 0;
  // Generate steps dynamically based on the score
  const steps1 = Array.from(
    { length: score },
    (_, i) => sampleSteps[i % sampleSteps.length]
  );
  const steps = categoryData && categoryData.length > 0 ? categoryData : [];
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setActiveStep(0); // Reset to the first step when expanding
    }
  };

  const handleToggleVisibility = (stepIndex) => {
    const isHidden = hiddenSteps[stepIndex];
    setHiddenSteps({ ...hiddenSteps, [stepIndex]: !isHidden });
    // If you need to call an API to update visibility, do so here:
    const stepId =
      steps[stepIndex]?.questionary_id ||
      steps[stepIndex]?.questions?.question_id;
    const visibility = !isHidden;
    updateStepVisibilityAPI(stepId, visibility);
  };

  const handleNextStep = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (activeStep > 0) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const updateStepVisibilityAPI = async (stepId, visibility) => {
    try {
      await fetch(`/api/step-visibility/${stepId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isVisible: visibility }),
      });
    } catch (error) {
      console.error('Error updating step visibility:', error);
    }
  };

  const currentStep = steps[activeStep]; // Get the active step data
  return (
    <Box className="collapsible-card">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        // onClick={toggleExpand}
        sx={{ cursor: 'pointer' }}
        className="collapsible-card-header"
      >
        <Typography
          className="title"
          sx={{
            color: '#333',
            display: 'flex',
            flexDirection: 'column',
            paddingLeft: '16px',
            flex: 1,
          }}
        >
          {title}
          <span style={{ color: '#787575' }}>
            {points} {transitionData?.checkPoints}
          </span>
        </Typography>
        <Typography className="score" style={{ lineHeight: '22px' }}>
          <span style={{ fontSize: '12px', color: '#000' }}>
            {transitionData?.rating}
          </span>{' '}
          <br />
          <span
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: score <= 4 ? 'red' : score <= 7 ? 'orange' : 'green',
            }}
          >
            {score}
          </span>{' '}
          / 10
        </Typography>
      </Box>

      {/* Collapsible Content */}
      {isExpanded && (
        <Box
          sx={{
            marginTop: '16px',
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
          }}
        >
          {/* Step Content */}
          <Box
            sx={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: hiddenSteps[activeStep] ? '#f9f9f9' : '#fff',
            }}
          >
            {/* Step Header with Toggle */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="body1" fontWeight="bold">
                {`Question ${activeStep + 1} of ${steps.length}`}
              </Typography>
              {/* <Button
                variant="outlined"
                size="small"
                color={hiddenSteps[activeStep] ? "success" : "error"}
                onClick={() => handleToggleVisibility(activeStep)}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  padding: "4px 8px",
                }}
              >
                {hiddenSteps[activeStep] ? "Show" : "Hide"}
              </Button> */}
              {currentStep?.questions.uploads?.audio?.path && (
                <Box className="audio-container" mt={2}>
                  <audio controls>
                    <source
                      src={currentStep?.questions.uploads?.audio?.path}
                      type="audio/mpeg"
                    />
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              )}
            </Box>

            {/* Step Content */}
            {!hiddenSteps[activeStep] && currentStep && (
              <Box mt={2}>
                {/* Step Description */}
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {currentStep?.questions.question_description}
                </Typography>
                {currentStep.questions?.select_options &&
                  currentStep.questions?.select_options?.choices && (
                    <Box mt={1}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {Object.values(
                          currentStep.questions.select_options.choices || {}
                        )
                          .filter((choice) => choice.selected)
                          .map((choice) => choice.choice)
                          .join(', ')}
                      </Typography>
                    </Box>
                  )}
                {/* Images Carousel */}
                {currentStep.questions.uploads?.photo?.path &&
                  currentStep.questions.uploads?.photo?.path !==
                    'PathToFile' && (
                    <Box mt={2}>
                      <ImageGallery
                        items={[
                          {
                            original:
                              currentStep.questions.uploads?.photo?.path,
                            thumbnail:
                              currentStep.questions.uploads?.photo?.path,
                          },
                        ]}
                        showThumbnails={false}
                        showPlayButton={false}
                        showFullscreenButton={false}
                        thumbnailPosition="bottom"
                        showBullets={false}
                        showNav={true}
                        slideInterval={2000}
                      />
                    </Box>
                  )}

                {/* Video */}
                {currentStep?.questions.uploads?.video?.path && (
                  <Box className="video-container" mt={2}>
                    <iframe
                      width="100%"
                      height="300px"
                      src={currentStep?.questions.uploads?.video?.path}
                      title={`Video for Step ${activeStep + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </Box>
                )}

                {/* Render Sub-question if available */}
                {currentStep.questions.sub_question && (
                  <Box mt={2} pl={2} borderLeft="2px solid #ccc">
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, fontStyle: 'italic' }}
                    >
                      {currentStep.questions.sub_question.question_description}
                    </Typography>

                    {currentStep.questions.sub_question?.select_options &&
                      currentStep.questions.sub_question?.select_options
                        ?.choices && (
                        <Box mt={1}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 'bold' }}
                          >
                            {Object.values(
                              currentStep.questions.sub_question?.sub_question
                                ?.select_options.choices || {}
                            )
                              .filter((choice) => choice.selected)
                              .map((choice) => choice.choice)
                              .join(', ')}
                          </Typography>
                        </Box>
                      )}
                    {/* Sub-question image */}
                    {currentStep.questions.sub_question.photo_note_url &&
                      currentStep.questions.sub_question.image_note_url !==
                        'PathToFile' && (
                        <Box mt={1}>
                          <img
                            loading="lazy"
                            src={
                              currentStep.questions.sub_question.photo_note_url
                            }
                            alt="Sub-question"
                            style={{ maxWidth: '100%' }}
                          />
                        </Box>
                      )}
                    {/* Sub-question video */}
                    {currentStep.questions.sub_question.video_note_url &&
                      currentStep.questions.sub_question.video_note_url !==
                        'PathToFile' && (
                        <Box className="video-container" mt={1}>
                          <iframe
                            width="100%"
                            height="300px"
                            src={
                              currentStep.questions.sub_question.video_note_url
                            }
                            title="Sub-question Video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </Box>
                      )}
                    {/* Sub-question audio */}
                    {/* {currentStep.questions.sub_question.audio_note_url && (
                      <Box mt={1}>
                        <a href={currentStep.questions.sub_question.audio_note_url} target="_blank" rel="noopener noreferrer">
                          Listen to Sub-question Audio Note
                        </a>
                      </Box>
                    )} */}
                  </Box>
                )}

                {currentStep.questions.sub_question?.sub_question && (
                  <Box mt={2} pl={2} borderLeft="2px solid #ccc">
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, fontStyle: 'italic' }}
                    >
                      {
                        currentStep.questions.sub_question?.sub_question
                          .question_description
                      }
                    </Typography>

                    {currentStep.questions.sub_question?.sub_question
                      ?.select_options &&
                      currentStep.questions.sub_question?.sub_question
                        ?.select_options?.choices && (
                        <Box mt={1}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 'bold' }}
                          >
                            {Object.values(
                              currentStep.questions.sub_question?.sub_question
                                .select_options.choices || {}
                            )
                              .filter((choice) => choice.selected)
                              .map((choice) => choice.choice)
                              .join(', ')}
                          </Typography>
                        </Box>
                      )}
                    {/* Sub-question image */}
                    {currentStep.questions.sub_question?.sub_question
                      .photo_note_url &&
                      currentStep.questions.sub_question?.sub_question
                        .image_note_url !== 'PathToFile' && (
                        <Box mt={1}>
                          <img
                            loading="lazy"
                            src={
                              currentStep.questions.sub_question?.sub_question
                                .photo_note_url
                            }
                            alt="Sub-question"
                            style={{ maxWidth: '100%' }}
                          />
                        </Box>
                      )}
                    {/* Sub-question video */}
                    {currentStep.questions.sub_question?.sub_question
                      .video_note_url &&
                      currentStep.questions.sub_question?.sub_question
                        .video_note_url !== 'PathToFile' && (
                        <Box className="video-container" mt={1}>
                          <iframe
                            width="100%"
                            height="300px"
                            src={
                              currentStep.questions.sub_question?.sub_question
                                .video_note_url
                            }
                            title="Sub-question Video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </Box>
                      )}
                    {/* Sub-question audio */}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Navigation */}
          <MobileStepper
            steps={steps.length}
            position="static"
            activeStep={activeStep}
            nextButton={
              <Button
                size="small"
                onClick={handleNextStep}
                disabled={activeStep === steps.length - 1}
              >
                Next
                <NavigateNext />
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={handlePreviousStep}
                disabled={activeStep === 0}
              >
                <NavigateBefore />
                Back
              </Button>
            }
            variant=""
          />
        </Box>
      )}

      {/* Show More / Show Less Button */}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          onClick={toggleExpand}
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            textTransform: 'none',
            padding: '4px 12px',
            backgroundColor: '#398070',
            fontFamily: 'Manrope, sans-serif',
            color: '#fff',
            borderRadius: '16px',
            '&:hover': {
              backgroundColor: '#2f6657',
            },
          }}
          className="showButton"
        >
          {isExpanded ? transitionData?.showLess : transitionData?.showMore}
        </Button>
      </Box>
    </Box>
  );
};

const HealthReport = ({ cardData, transitionData }) => {
  const [showFullReport, setShowFullReport] = useState(false);
  const { categoryData } = useContext(PortalContext);
  const combinedCategories = useCombinedCategories(cardData, categoryData);
  return (
    <section className="health-report">
      <div className="report-header">
        {combinedCategories.map((category) => (
          <div className="parameter-card" key={category.key}>
            <CollapsibleCard
              key={category.key}
              title={category.title}
              categoryKey={category.key}
              points={category.total_questions}
              categoryData={category?.inspectionQuestions}
              cardData={cardData}
              transitionData={transitionData}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

const FaultCodeCard = ({
  title,
  initialErrorCodes,
  isPublished,
  transitionData,
}) => {
  const { errorCodes, addErrorCode } = useContext(PortalContext);
  const [isAdding, setIsAdding] = useState(false);
  const [newPortNumber, setNewPortNumber] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => {
    if (Array.isArray(initialErrorCodes)) {
      initialErrorCodes.forEach((code) => {
        if (
          !errorCodes.some(
            (ec) =>
              ec.description === code.description &&
              ec.portNumber === code.portNumber
          )
        ) {
          addErrorCode(code);
        }
      });
    }
  }, [initialErrorCodes, addErrorCode, errorCodes]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleOpenAdd = () => {
    setIsAdding(true);
  };

  const handleCloseAdd = () => {
    setIsAdding(false);
    setNewPortNumber('');
    setNewDescription('');
  };

  const hasCategoryData = (error) => {
    return (
      (error.possibleCauses && error.possibleCauses.length > 0) ||
      (error.symptoms && error.symptoms.length > 0) ||
      (error.solutions && error.solutions.length > 0)
    );
  };

  const totalErrors = Object.values(initialErrorCodes || {}).flat().length;

  return (
    <Box className="collapsible-card-obg">
      {/* Card Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        onClick={toggleExpand}
        sx={{ cursor: 'pointer' }}
        className="collapsible-card-header_OBD"
      >
        <Typography
          className="title"
          sx={{
            fontWeight: 500,
            color: '#333',
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          {title}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          className="collapsible-card-header_OBD_errors"
        >
          <Typography
            sx={{
              fontWeight: 500,
              color: 'red',
              marginRight: '8px',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            {totalErrors} {transitionData?.errorsFound}
          </Typography>
        </Box>
      </Box>
      {/* Expandable Content */}
      {isExpanded && (
        <Box
          sx={{
            marginTop: '16px',
            // padding: '16px',
            // backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            transition: 'max-height 0.3s ease-in-out',
          }}
        >
          {Object.entries(initialErrorCodes).map(([module, errors]) => (
            <Accordion
              key={module}
              sx={{
                mb: 1,
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: 'none',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      color: '#000',
                      fontFamily: 'Manrope, sans-serif',
                    }}
                  >
                    {transitionData?.faults}{' '}
                    <span style={{ color: '#d32f2f' }}>{module}</span>
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ flexDirection: 'column' }}>
                {errors.map((error, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      mb: 2,
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: 'none',
                    }}
                  >
                    {/* Port Number Accordion Summary */}
                    <AccordionSummary
                      expandIcon={
                        hasCategoryData(error) ? <ExpandMore /> : null
                      }
                    >
                      <Box>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="primary"
                            gutterBottom
                          >
                            {error.portNumber}
                          </Typography>
                          {error.status && (
                            <Typography
                              variant="body2"
                              style={{ color: '#b71c1c', fontSize: '12px' }}
                              className="obdDescE"
                            >
                              {error.status}
                            </Typography>
                          )}
                        </div>

                        <Typography variant="body2" className="obdDescE">
                          {error.description || 'No description available.'}
                        </Typography>
                      </Box>
                    </AccordionSummary>

                    {/* Port Number Accordion Details */}
                    {hasCategoryData(error) && (
                      <AccordionDetails>
                        {error.possibleCauses?.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="body2" fontWeight="bold">
                              Possible Causes:
                            </Typography>
                            <ul>
                              {error.possibleCauses.map((cause, idx) => (
                                <li
                                  key={idx}
                                  style={{
                                    paddingBottom: '4px',
                                    paddingInlineStart: '0',
                                    fontSize: '14px',
                                  }}
                                >
                                  <span className="mr-2 font-bold text-black">
                                    •
                                  </span>{' '}
                                  {cause}
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )}
                        {error.symptoms?.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="body2" fontWeight="bold">
                              Symptoms:
                            </Typography>
                            <ul>
                              {error.symptoms.map((symptom, idx) => (
                                <li
                                  key={idx}
                                  style={{
                                    paddingBottom: '4px',
                                    paddingInlineStart: '0',
                                    fontSize: '14px',
                                  }}
                                >
                                  <span className="mr-2 font-bold text-black">
                                    •
                                  </span>{' '}
                                  {symptom}
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )}
                        {error.solutions?.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="body2" fontWeight="bold">
                              Solutions:
                            </Typography>
                            <ul>
                              {error.solutions.map((solution, idx) => (
                                <li
                                  key={idx}
                                  style={{
                                    paddingBottom: '4px',
                                    paddingInlineStart: '0',
                                    fontSize: '14px',
                                  }}
                                >
                                  <span className="mr-2 font-bold text-black">
                                    •
                                  </span>{' '}
                                  {solution}
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )}
                      </AccordionDetails>
                    )}
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          onClick={toggleExpand}
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            textTransform: 'none',
            padding: '4px 12px',
            backgroundColor: '#eb6d474f',
            position: 'absolute',
            fontFamily: 'Manrope, sans-serif',
            color: '#ff0000',
            borderRadius: '8px 0',
            bottom: 0,
            right: 0,
            '&:hover': {
              backgroundColor: '#eb6d474f',
            },
          }}
          className="showButton"
        >
          {isExpanded
            ? transitionData?.hideFaultCodes
            : transitionData?.showFaultCodes}
        </Button>
      </Box>
    </Box>
  );
};
const RepairsEstimate = ({ transitionData }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState([]);
  const [modalTitle, setModalTitle] = useState('');

  const majorRepairs = [
    {
      type: 'Labour',
      description: 'Engine Misfiring,Need to Check Head / Spark Plug',
      cost: 'INR 2000',
    },
    {
      type: 'Spare',
      description: 'Brake Replacement,Change front and rear brake pads',
      cost: 'INR 3000',
    },
  ];

  const minorRepairs = [
    {
      type: 'Spare',
      description: 'AC Filter Cleaning,Clean AC filter for better airflow',
      cost: 'INR 500',
    },
    {
      type: 'Labour',
      description: 'Windshield Wiper Replacement',
      cost: 'INR 300',
    },
  ];

  const handleOpenModal = (repairType) => {
    if (repairType === 'Major Repairs') {
      setSelectedEstimate(majorRepairs);
      setModalTitle(transitionData?.majorRepairEstimate);
    } else if (repairType === 'Minor Repairs') {
      setSelectedEstimate(minorRepairs);
      setModalTitle(transitionData?.minorRepairEstimate);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <EstimateCard
          title="Major Repairs"
          price="Rs 15000"
          handleOpenModal={() => handleOpenModal('Major Repairs')}
        />
        <EstimateCard
          title="Minor Repairs"
          price="Rs 1000"
          handleOpenModal={() => handleOpenModal('Minor Repairs')}
        />
      </Box>

      <EstimateModal
        open={openModal}
        handleClose={handleCloseModal}
        estimates={selectedEstimate}
        title={modalTitle}
      />
    </>
  );
};

const NeoInfo = ({ transitionData }) => {
  return (
    <article className="neo-info">
      <section className="neo-grid">
        <div className="neo-grid-item">
          <span className="neo-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="28"
              fill="none"
            >
              <path
                fill="url(#phone_svg__a)"
                d="M21.083 3.5c0-1.93-1.644-3.5-3.666-3.5H4.583C2.561 0 .917 1.57.917 3.5v21c0 1.93 1.644 3.5 3.666 3.5h12.834c2.022 0 3.666-1.57 3.666-3.5zm-7.333 21c0 .481-.413.875-.917.875H9.167c-.505 0-.917-.394-.917-.875s.412-.875.917-.875h3.666c.504 0 .917.394.917.875m-9.167-21h12.834V21H4.583z"
              />
              <defs>
                <linearGradient
                  id="phone_svg__a"
                  x1="3.208"
                  x2="22.987"
                  y1="-16.625"
                  y2="24.054"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#B1B1B1" />
                  <stop offset="1" stopColor="#3C3C3C" />
                </linearGradient>
              </defs>
            </svg>
          </span>
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-14 oui-typography--weight-800 oui-typography--decoration-none oui-text-grey-100">
            {transitionData?.easy}
          </span>
          <br />
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-14 oui-typography--weight-500 oui-typography--decoration-none oui-text-grey-100">
            {transitionData?.made}
          </span>
        </div>

        <div className="neo-grid-item">
          <span className="neo-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="22"
              fill="none"
            >
              <path
                fill="url(#track_svg__a)"
                d="M3 2C3 1.17 2.33.5 1.5.5S0 1.17 0 2v15.75a3.75 3.75 0 0 0 3.75 3.75H22.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5H3.75a.75.75 0 0 1-.75-.75zm19.06 4.06a1.502 1.502 0 0 0-2.124-2.124L15 8.876l-2.69-2.69a1.5 1.5 0 0 0-2.124 0l-5.25 5.25a1.502 1.502 0 0 0 2.123 2.123l4.191-4.186 2.69 2.691a1.5 1.5 0 0 0 2.124 0l6-6z"
              />
              <defs>
                <linearGradient
                  id="track_svg__a"
                  x1="21.273"
                  x2="10.707"
                  y1="-11.969"
                  y2="22.515"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#B1B1B1" />
                  <stop offset="1" stopColor="#3C3C3C" />
                </linearGradient>
              </defs>
            </svg>
          </span>
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-14 oui-typography--weight-800 oui-typography--decoration-none oui-text-grey-100">
            {transitionData?.track}
          </span>
          <br />
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-14 oui-typography--weight-500 oui-typography--decoration-none oui-text-grey-100">
            {transitionData?.trackMade}
          </span>
        </div>

        <div className="neo-grid-item">
          <span className="neo-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
            >
              <path
                fill="url(#search_svg__a)"
                fillRule="evenodd"
                d="M10.736 2.512c-4.436 0-8.03 3.657-8.03 8.166s3.594 8.166 8.03 8.166c4.435 0 8.029-3.657 8.029-8.166s-3.594-8.166-8.03-8.166M.75 10.678C.75 5.067 5.222.52 10.736.52s9.985 4.547 9.985 10.158-4.471 10.158-9.985 10.158S.75 16.29.75 10.678m17.682 7.44a.966.966 0 0 1 1.384 0l3.147 3.2a1.01 1.01 0 0 1 0 1.41.966.966 0 0 1-1.383 0l-3.147-3.201a1.01 1.01 0 0 1 0-1.409"
                clipRule="evenodd"
              />
              <defs>
                <linearGradient
                  id="search_svg__a"
                  x1="20.693"
                  x2="8.086"
                  y1="-12.84"
                  y2="23.161"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#B1B1B1" />
                  <stop offset="1" stopColor="#3C3C3C" />
                </linearGradient>
              </defs>
            </svg>
          </span>
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-14 oui-typography--weight-800 oui-typography--decoration-none oui-text-grey-100">
            {transitionData?.report}
          </span>
          <br />
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-14 oui-typography--weight-500 oui-typography--decoration-none oui-text-grey-100">
            {transitionData?.reportMade}
          </span>
        </div>
      </section>
    </article>
  );
};

const Availability = () => {
  return (
    <section className="availability">
      <div
        className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-100"
        style={{ color: '#fff' }}
      >
        We are available in
      </div>

      <div className="cities">
        <div className="city">
          <img
            loading="lazy"
            src={delhi}
            alt="delhi"
            height={48}
          />
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-05">
            Delhi NCR
          </span>
        </div>

        <div className="city">
          <img
            loading="lazy"
            src={mumbai}
            alt="mumbai"
            height={48}
          />
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-05">
            Mumbai
          </span>
        </div>

        <div className="city">
          <img
            loading="lazy"
            src={hyderabad}
            alt="hyderabad"
            height={48}
          />
          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-05">
            Hyderabad
          </span>
        </div>

        <div className="city">
          <img
            loading="lazy"
            src={bangalore}
            alt="bangalore"
            height={48}
            className="icon-white"
          />

          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-05">
            Bangalore
          </span>
        </div>
        <div className="city">
          <img
            loading="lazy"
            src={chennai}
            alt="chennai"
            height={48}
          />

          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-05">
            Chennai
          </span>
        </div>
        <div className="city">
          <img
            loading="lazy"
            src={pune}
            alt="pune"
            height={48}
            className="icon-white"
          />

          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-05">
            Pune
          </span>
        </div>
        <div className="city">
          <img
            loading="lazy"
            src={gurguaon}
            alt="pune"
            height={58}
          />

          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-05">
            Gurgaon
          </span>
        </div>
        <div className="city">
          <img
            loading="lazy"
            src={noida}
            alt="noida"
            height={58}
          />

          <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-12 oui-typography--weight-600 oui-typography--decoration-none oui-text-grey-05">
            Noida
          </span>
        </div>
      </div>
    </section>
  );
};

const BrandInfo = ({ country }) => {
  return (
    <section className="brand-info">
      <div className="trust-markers">
        <div className="header__logo">
          <img
            loading="lazy"
            src={WiseDriveLogoNew}
            alt="Orange Health Labs"
            className="header__logo-img"
            style={{ height: '80px', width: '230px' }}
          />
        </div>
      </div>

      <div className="download-app-buttons">
        <span className="oui-typography oui-typography--font-family-manrope oui-typography--transform-none oui-typography--size-14 oui-typography--weight-600 oui-typography--decoration-none oui-text-white">
          {country === 'IN' || !country ? (
            <>
              Wisedrive Technologies Pvt Ltd,
              <br />
              WeWork, Roshini Tech Hub, Marathahalli Mn Road,
              <br />
              Bangalore, Karnataka, India - 560037,
              <br />
              https://www.wisedrive.com
            </>
          ) : (
            <>
              Wisedrive Technologies (M) Sdn. Bhd.,
              <br />
              Lot 2F, Jalan 215, Seksyen 51, Petaling Jaya,
              <br />
              Selangor, Malaysia 46050`,
              <br />
              https://www.wisedrive.com
            </>
          )}
        </span>
      </div>
    </section>
  );
};

const SampleReportNew = () => {
  const [openModal, setOpenModal] = useState(false);
  // Default language (Malay)
  const [translatedReportData, setTranslatedReportData] = useState(null);
  const [translatedTransitionData, setTranslatedTransitionData] =
    useState(null);
  const [loading, setLoading] = useState(false);

  const {
    reportViewData,
    setCategoryData,
    dentsAndScratchesMap,
    parsedTyreLife,
  } = useContext(PortalContext);
  const { pageData, setCountry } = useContext(MyContext);
  const [reportData, setReportData] = useState(reportViewData); // State to store report data
  const languageMap = {
    IN: 'en',
    MY: 'ms',
    TH: 'th', // Add more as needed
  };
  const [language, setLanguage] = useState(() => {
    const code = reportData?.countryCode?.toUpperCase?.();
    return languageMap[code] || 'en';
  });
  useEffect(() => {
    // setCategoryData(reportData?.inspectionData);
    setCountry(reportData?.countryCode);
  }, [reportData, setCategoryData]);

  // useEffect(() => {
  //   if (country) {
  //     setLanguage(country === "IN" ? "en" : "ms");
  //   }
  // }, [country]);

  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage !== null) {
      setLanguage(newLanguage);
    }
  };

  useEffect(() => {
    const translateData = async () => {
      if (!language || !reportData || !pageData?.reportData) {
        return;
      }

      try {
        // Skip translation for India (IN), use original data
        if (reportData?.countryCode === 'IN') {
          setTranslatedReportData(reportData);
          setTranslatedTransitionData(pageData.reportData);
          setCategoryData(reportData?.inspectionData);

          return;
        }

        setLoading(true);

        // Translate reportData for Malay
        const translatedReport = { ...reportData };
        translatedReport.customerName = await translateText(
          reportData?.customerName,
          language
        );
        translatedReport.carRegistrationNumber =
          reportData?.carRegistrationNumber; // No translation
        translatedReport.inspectionDetails = await translateNestedObject(
          reportData?.inspectionDetails,
          language
        );
        translatedReport.vehicleInformation = await translateNestedObject(
          reportData?.vehicleInformation,
          language
        );
        translatedReport.keyInformation = await translateNestedObject(
          reportData?.keyInformation,
          language
        );
        translatedReport.rtoInformation = await translateNestedObject(
          reportData?.rtoInformation,
          language
        );
        translatedReport.marketValue = await translateText(
          reportData?.marketValue || reportData?.aiInsights?.marketValue,
          language
        );
        translatedReport.recommendedToBuy = await translateText(
          reportData?.recommendedToBuy ||
            reportData?.aiInsights?.recommendedToBuy,
          language
        );
        translatedReport.summary = await translateText(
          reportData?.summary || reportData?.aiInsights?.summary,
          language
        );
        translatedReport.additionalData = await translateNestedObject(
          reportData?.additionalData,
          language
        );
        translatedReport.additionalChallans = await translateArray(
          reportData?.additionalChallans,
          language
        );
        translatedReport.obdScanningErrors = await translateArray(
          reportData?.obdScanningErrors,
          language
        );
        translatedReport.additionalOBDScanningErrors = await translateArray(
          reportData?.additionalOBDScanningErrors,
          language
        );
        translatedReport.inspectionData = await translateInspectionData(
          reportData?.inspectionData,
          language
        );
        translatedReport.transmissionType = await translateText(
          reportData?.transmissionType,
          language
        );
        translatedReport.kmDriven = reportData?.kmDriven; // No translation
        translatedReport.aiInsights = await translateNestedObject(
          reportData?.aiInsights,
          language
        );
        translatedReport.reportPublishedURL = reportData?.reportPublishedURL; // No translation
        translatedReport.carRegPlatePhoto = reportData?.carRegPlatePhoto; // No translation
        translatedReport.dashboardPhoto = reportData?.dashboardPhoto; // No translation
        translatedReport.pdfReportAttachment = reportData?.pdfReportAttachment; // No translation
        translatedReport.pdfAttachmentName = reportData?.pdfAttachmentName; // No translation
        translatedReport.isPublished = reportData?.isPublished; // No translation
        translatedReport.reportPublishedDate = reportData?.reportPublishedDate; // No translation
        translatedReport.countryCode = reportData?.countryCode; // No translation

        // Translate transitionData for Malay
        const translatedTransition = {
          ...pageData.reportData,
          healthReportTitle: await translateText('Health Report', language),
          viewFullReport: await translateText('View Full Report', language),
          tyreLifeDetails: await translateText('Tyre Life Details', language),
          noTyreLifeData: await translateText(
            'No tyre life data available.',
            language
          ),
          majorAccidentInfo: await translateText(
            'Major Accident Information',
            language
          ),
          majorAccidentInfoContent: await translateText(
            "Based on physical inspection, the mechanic didn't find any evidence of an accident. For further information, please contact the authorized service center or insurance provider.",
            language
          ),
          details: await translateText('Details', language),
          estimate: await translateText('Estimate', language),
          valid: await translateText('Valid', language),
          expired: await translateText('Expired', language),
          notAvailable: await translateText('Not Available', language),
          clickForMoreInfo: await translateText(
            'Click for more information',
            language
          ),
          expiresIn: await translateText('Expires in', language),
          days: await translateText('days', language),
          daysAgo: await translateText('days ago', language),
        };

        // Translate remaining transitionData keys
        for (const key in pageData.reportData) {
          if (!(key in translatedTransition)) {
            translatedTransition[key] = await translateText(
              pageData.reportData[key],
              language
            );
          }
        }

        setTranslatedReportData(translatedReport);
        setTranslatedTransitionData(translatedTransition);
        setCategoryData(translatedReport.inspectionData);
      } catch (error) {
        console.error('Translation error:', error);
        // Fallback to original data on error
        setTranslatedReportData(reportData);
        setTranslatedTransitionData(pageData.reportData);
        setCategoryData(reportData?.inspectionData);
      } finally {
        setLoading(false);
      }
    };

    translateData();
  }, [reportData, pageData, language, setCategoryData, setLanguage]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const mergeAndFilterErrors = (additionalErrors, baseErrors) => {
    const allErrors = [...(additionalErrors || []), ...(baseErrors || [])];
    const uniqueErrors = allErrors.filter(
      (error, index, self) =>
        index ===
        self.findIndex(
          (e) =>
            e.portNumber === error.portNumber &&
            e.description === error.description
        )
    );
    return uniqueErrors;
  };

  const processedErrorCodes = mergeAndFilterErrors(
    translatedReportData?.additionalOBDScanningErrors,
    translatedReportData?.obdScanningErrors
  );

  const groupErrorsByModule = (errors) => {
    return errors.reduce((grouped, error) => {
      const moduleName = error.module || 'Unknown';
      if (!grouped[moduleName]) {
        grouped[moduleName] = [];
      }
      grouped[moduleName].push(error);
      return grouped;
    }, {});
  };

  const groupedErrorCodes = groupErrorsByModule(processedErrorCodes);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      <div className="page-wrapper">
        <Header
          regNo={reportData?.carRegistrationNumber}
          language={language}
          handleLanguageChange={handleLanguageChange}
          country={reportData?.countryCode}
        />
        <SummaryCard
          handleOpenModal={handleOpenModal}
          summaryData={translatedReportData}
          inspectionDetails={translatedReportData?.inspectionDetails}
          transitionData={translatedTransitionData}
        />
        <InspectionSummary
          summary={
            translatedReportData?.summary ||
            translatedReportData?.aiInsights?.summary
          }
          vehicleInfo={{
            owners:
              translatedReportData?.vehicleInformation?.owners ||
              translatedReportData?.additionalData?.ownerCount ||
              '-',
            make:
              translatedReportData?.vehicleInformation?.make ||
              translatedReportData?.additionalData?.make ||
              '-',
            model:
              translatedReportData?.vehicleInformation?.model ||
              translatedReportData?.additionalData?.model ||
              '-',
            year:
              translatedReportData?.vehicleInformation?.year ||
              translatedReportData?.additionalData?.year ||
              '-',
            mfgDate:
              translatedReportData?.vehicleInformation?.mfgDate ||
              translatedReportData?.additionalData?.mfgDate ||
              '-',
            regNo:
              translatedReportData?.vehicleInformation?.regNo ||
              translatedReportData?.additionalData?.regNo ||
              '-',
            fuel:
              translatedReportData?.vehicleInformation?.fuel ||
              translatedReportData?.additionalData?.fuel ||
              '-',
            regDate:
              translatedReportData?.vehicleInformation?.regDate ||
              translatedReportData?.additionalData?.regDate ||
              '-',
            engineCC:
              translatedReportData?.vehicleInformation?.engineCC ||
              translatedReportData?.additionalData?.engineCC ||
              '-',
            transmission: translatedReportData?.transmissionType || '-',
            colour:
              translatedReportData?.vehicleInformation?.colour ||
              translatedReportData?.additionalData?.color ||
              '-',
            engineNumber:
              translatedReportData?.vehicleInformation?.engineNumber ||
              translatedReportData?.additionalData?.engineNumber ||
              '-',
            chassisNumber:
              translatedReportData?.vehicleInformation?.chassisNumber ||
              translatedReportData?.additionalData?.chassisNumber ||
              '-',
          }}
          transitionData={translatedTransitionData}
        />
        <VehicleInformationModal
          open={openModal}
          handleClose={handleCloseModal}
        />
        <KeyInformation
          initialKeyInfo={translatedReportData?.keyInformation}
          insuranceData={translatedReportData?.rtoInformation?.insurance}
          dentsData={dentsAndScratchesMap}
          majorRepairData={
            translatedReportData?.keyInformation?.majorRepair ??
            reportData?.keyInformation?.majorRepair
          }
          minorRepairData={
            translatedReportData?.keyInformation?.minorRepair ??
            reportData?.keyInformation?.minorRepair
          }
          tyreLifeData={parsedTyreLife}
          kmsDriven={translatedReportData?.kmDriven}
          reportData={translatedReportData}
          transitionData={translatedTransitionData}
        />
        <AdditionalInformation
          rtoData={translatedReportData?.rtoInformation}
          additionalChallans={
            translatedReportData?.additionalChallans?.length > 0
              ? translatedReportData?.additionalChallans
              : translatedReportData?.rtoInformation?.challans
          }
          transitionData={translatedTransitionData}
        />
        {translatedReportData?.obdUrl ? (
          <Box display="flex" alignItems="center" gap="8px">
            <Typography
              variant="subtitle1"
              sx={{ fontFamily: 'Manrope, sans-serif' }}
            >
              OBD Report URL:
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                wordBreak: 'break-all',
                color: '#333',
              }}
            >
              {translatedReportData?.obdUrl}
            </Typography>
          </Box>
        ) : (
          <FaultCodeCard
            title={translatedTransitionData?.obd2ScanningReport}
            initialErrorCodes={groupedErrorCodes}
            transitionData={translatedTransitionData}
          />
        )}
        {/* <RepairsEstimate /> */}
        <HealthReport
          cardData={
            translatedReportData?.additionalData
              ? translatedReportData.additionalData.categoryWiseRating
              : translatedReportData?.aiInsights?.categoryWiseRating
          }
          pdfData={translatedReportData?.pdfReportAttachment}
          transitionData={translatedTransitionData}
        />
        <NeoInfo transitionData={translatedTransitionData} />
        {/* <Availability /> */}
        <BrandInfo country={reportData?.countryCode} />
      </div>
    </>
  );
};

export default SampleReportNew;
